import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Flame, Search, Handshake, ArrowRight, MapPin } from 'lucide-react';
import api from '@/lib/api';
import OptimizedImage from '@/components/OptimizedImage';

const sora = { fontFamily: 'Sora, sans-serif' };

const MOCK_WANTED = [
  { title: 'Cama matrimonial', people: 8, location: 'Ocoyoacac', hot: true },
  { title: 'Libros de prepa', people: 6, location: 'Santa Maria', hot: false },
  { title: 'Bicicleta de nino', people: 4, location: 'El Pedregal', hot: false },
  { title: 'Estufa de gas', people: 3, location: 'Cholula', hot: false },
];

export default function DesktopRightPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [trendingItems, setTrendingItems] = useState([]);
  const [dealItem, setDealItem] = useState(null);

  useEffect(() => {
    api.get('/auth/me').then(r => setUser(r.data)).catch(() => {});
    api.get('/items?limit=5').then(r => {
      const items = r.data?.data || [];
      setTrendingItems(items.slice(0, 3));
      if (items.length > 0) setDealItem(items[0]);
    }).catch(() => {});
  }, []);

  const getMediaUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${url}`);

  return (
    <aside data-testid="desktop-right-panel"
      className="hidden lg:flex flex-col w-[280px] shrink-0 h-[calc(100vh-48px)] sticky top-[48px] overflow-y-auto scrollbar-hide py-4 px-3 space-y-4"
      style={sora}
    >
      {/* Deal del Dia */}
      {dealItem && (
        <div className="rounded-2xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <span className="text-xs font-bold" style={{ color: '#ef4444' }}>DEAL DEL DIA</span>
            <span className="text-[10px]" style={{ color: '#555' }}>Termina en 4h 23m</span>
          </div>
          <div className="px-3 pb-3">
            <div className="relative rounded-xl overflow-hidden mb-2.5" style={{ height: 130, border: '1px solid #1a1a1a' }}>
              <OptimizedImage
                src={getMediaUrl(dealItem.images?.[0] || dealItem.image_url)}
                alt={dealItem.title}
                thumbWidth={280}
                className="w-full h-full"
              />
              {dealItem.location && (
                <span className="absolute bottom-2 left-2 text-[9px] px-2 py-0.5 rounded-full flex items-center gap-0.5"
                  style={{ background: '#00000099', backdropFilter: 'blur(6px)', color: '#aaa' }}>
                  <MapPin className="w-2.5 h-2.5" />{dealItem.location}
                </span>
              )}
              <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: '#ef4444', color: '#fff' }}>-12%</span>
            </div>
            <h4 className="text-sm font-bold" style={{ color: '#d5d5d5' }}>{dealItem.title}</h4>
            <p className="text-lg font-black mt-0.5" style={{ color: '#f5c518' }}>
              ${dealItem.price?.toLocaleString()} <span className="text-xs line-through font-normal" style={{ color: '#555' }}>${Math.floor(dealItem.price * 1.12).toLocaleString()}</span>
            </p>
            <p className="text-[10px] mt-0.5" style={{ color: '#555' }}>
              @{dealItem.seller_name?.toLowerCase().replace(/\s/g, '_')} · {dealItem.location}
            </p>
            <button
              onClick={() => navigate(`/deal/${dealItem.item_id}`)}
              className="w-full mt-2.5 py-2 rounded-full text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
              style={{ background: 'linear-gradient(135deg, #f5c518, #d4a017)', color: '#000' }}>
              <Handshake className="w-3.5 h-3.5" /> Negociar ahora
            </button>
          </div>
        </div>
      )}

      {/* Se busca cerca de ti */}
      <div className="rounded-2xl p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold" style={{ color: '#d5d5d5' }}>
            <Search className="w-3 h-3 inline mr-1 -mt-0.5" />Se busca cerca de ti
          </span>
          <span className="text-[10px]" style={{ color: '#555' }}>Ver todo</span>
        </div>
        <p className="text-[10px] mb-3" style={{ color: '#555', lineHeight: 1.5 }}>
          Vecinos buscan estos articulos pero nadie los ofrece — oportunidad de venta.
        </p>
        <div className="space-y-2">
          {MOCK_WANTED.map((w, i) => (
            <div key={i} className="flex items-center gap-2.5 rounded-xl px-2.5 py-2"
              style={{ background: '#111', border: '1px solid #1a1a1a' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: '#1a1a1a' }}>
                <Search className="w-3.5 h-3.5" style={{ color: '#555' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: '#d5d5d5' }}>{w.title}</p>
                <p className="text-[9px]" style={{ color: '#555' }}>{w.people} personas · {w.location}</p>
              </div>
              {w.hot ? (
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#ef444420', color: '#ef4444' }}>HOT</span>
              ) : (
                <button onClick={() => navigate('/upload')}
                  className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: '#f5c51815', color: '#f5c518', border: '1px solid #f5c51830' }}>
                  Ofrecer
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tendencias */}
      <div className="rounded-2xl p-4" style={{ background: '#0f0f0f', border: '1px solid #1a1a1a' }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold" style={{ color: '#d5d5d5' }}>
            <TrendingUp className="w-3 h-3 inline mr-1 -mt-0.5" />Tendencias
          </span>
          <span className="text-[10px]" style={{ color: '#555' }}>Ver todo →</span>
        </div>
        <div className="space-y-3">
          {trendingItems.map((item, i) => (
            <button key={item.item_id}
              onClick={() => navigate(`/item/${item.item_id}`)}
              className="w-full flex items-center gap-2.5 text-left hover:opacity-80 transition-opacity">
              <span className="text-lg font-black w-5 text-center" style={{ color: '#333' }}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate" style={{ color: '#d5d5d5' }}>{item.title}</p>
                <p className="text-sm font-bold flex items-center gap-1" style={{ color: '#f5c518' }}>
                  ${item.price?.toLocaleString()}
                  {i === 1 && <Flame className="w-3 h-3" style={{ color: '#ef4444' }} />}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-2xl p-4 text-center" style={{ background: 'linear-gradient(135deg, #0f0a1a, #1a0f2e)', border: '1px solid #2a1f4a' }}>
        <p className="text-xs font-semibold" style={{ color: '#d5d5d5' }}>
          <Search className="w-3 h-3 inline mr-1 -mt-0.5" /> Algo para cambiar?
        </p>
        <button onClick={() => navigate('/upload')}
          className="w-full mt-2.5 py-2.5 rounded-full text-xs font-bold"
          style={{ background: 'linear-gradient(135deg, #f5c518, #d4a017)', color: '#000' }}>
          Publicar Ribete
        </button>
      </div>
    </aside>
  );
}
